import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ScorecardsService } from '../../api-client/api/scorecards.service';
import {
  ModulesScorecardsEndpointsContractsCourseAssignmentsAssignPlayersToCourseRequest,
  ModulesScorecardsEndpointsContractsCourseAssignmentsAssignPlayersToCourseResponse,
  ModulesScorecardsEndpointsContractsCourseAssignmentsBulkAssignRequest,
  ModulesScorecardsEndpointsContractsCourseAssignmentsBulkAssignResponse,
  ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentCourseResponse,
  ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentInfoResponse,
  ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentOperationError,
  ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentPlayerResponse,
  ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentPlayersResponse,
  ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentSummaryResponse,
  ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentCourseSummaryResponse,
} from '../../api-client/model/models';
import { LoggingService } from '../shared/logging/logging.service';
import {
  AssignPlayersRequest,
  BulkAssignmentStrategy,
  CourseAssignmentCourse,
  CourseAssignmentOperationResult,
  CourseAssignmentPlayer,
  CourseAssignmentSummary,
} from '../../models/courseAssignment';

/** Configuration for polling with retries after mutations */
interface PollingConfig {
  /** Maximum number of polling attempts */
  maxAttempts: number;
  /** Delay between polling attempts in milliseconds */
  delayMs: number;
  /** Expected number of assigned players to wait for */
  expectedAssignedCount: number;
}

@Injectable({ providedIn: 'root' })
export class CourseAssignmentService {
  private readonly api = inject(ScorecardsService);
  private readonly logger = inject(LoggingService);

  private tournamentId: string | null = null;
  private roundId: string | null = null;

  readonly players = signal<CourseAssignmentPlayer[]>([]);
  readonly courses = signal<CourseAssignmentCourse[]>([]);
  readonly summary = signal<CourseAssignmentSummary | null>(null);
  readonly loading = signal<boolean>(false);
  readonly mutating = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  readonly unassignedPlayers = computed(() => this.players().filter(player => !player.currentAssignment));
  readonly assignedCount = computed(() => this.players().length - this.unassignedPlayers().length);
  readonly totalPlayers = computed(() => this.players().length);

  async initialize(tournamentId: string, roundId: string): Promise<void> {
    this.tournamentId = tournamentId;
    this.roundId = roundId;
    await this.refreshAll();
  }

  async refreshAll(): Promise<void> {
    this.ensureContext();

    this.loading.set(true);
    this.error.set(null);

    try {
      const [playersResponse, coursesResponse, summaryResponse] = await Promise.all([
        firstValueFrom(
          this.api.getCourseAssignmentPlayersEndpoint(this.tournamentId!, this.roundId!, true)
        ),
        firstValueFrom(
          this.api.getRoundCoursesEndpoint(this.tournamentId!, this.roundId!)
        ),
        firstValueFrom(
          this.api.getCourseAssignmentSummaryEndpoint(this.tournamentId!, this.roundId!)
        ),
      ]);

      this.players.set(this.toPlayers(playersResponse));
      this.courses.set(this.toCourses(coursesResponse));
      this.summary.set(this.toSummary(summaryResponse));
    } catch (error) {
      this.logger.error('CourseAssignmentService', 'Failed to load course assignment data', error);
      this.error.set('Unable to load course assignment data.');
      throw error;
    } finally {
      this.loading.set(false);
    }
  }

  async refreshPlayers(): Promise<void> {
    this.ensureContext();
    const response = await firstValueFrom(
      this.api.getCourseAssignmentPlayersEndpoint(this.tournamentId!, this.roundId!, true)
    );
    this.players.set(this.toPlayers(response));
  }

  async refreshCourses(): Promise<void> {
    this.ensureContext();
    const response = await firstValueFrom(
      this.api.getRoundCoursesEndpoint(this.tournamentId!, this.roundId!)
    );
    this.courses.set(this.toCourses(response));
  }

  async refreshSummary(): Promise<void> {
    this.ensureContext();
    const response = await firstValueFrom(
      this.api.getCourseAssignmentSummaryEndpoint(this.tournamentId!, this.roundId!)
    );
    this.summary.set(this.toSummary(response));
  }

  async assignPlayers(request: AssignPlayersRequest): Promise<CourseAssignmentOperationResult> {
    if (request.playerIds.length === 0) {
      return { assignedCount: 0, skippedCount: 0, errors: [] };
    }

    this.ensureContext();
    this.mutating.set(true);

    try {
      const currentAssignedCount = this.assignedCount();
      const payload: ModulesScorecardsEndpointsContractsCourseAssignmentsAssignPlayersToCourseRequest = {
        roundTournamentCourseId: request.roundTournamentCourseId,
        playerIds: request.playerIds,
        overwriteExisting: request.overwriteExisting,
      };

      const response = await firstValueFrom(
        this.api.assignPlayersToCourseEndpoint(payload)
      );

      // Calculate expected count: current + newly assigned (minus any that were already assigned if overwriting)
      const expectedAssignedCount = currentAssignedCount + (response.assignedCount ?? 0);

      // Poll until the expected count is reached (event-driven scorecard creation may take time)
      await this.pollUntilConsistent({
        maxAttempts: 5,
        delayMs: 500,
        expectedAssignedCount,
      });

      return this.toOperationResult(response);
    } catch (error) {
      this.logger.error('CourseAssignmentService', 'Failed to assign players to course', error);
      throw error;
    } finally {
      this.mutating.set(false);
    }
  }

  async bulkAssign(strategy: BulkAssignmentStrategy, clearExisting: boolean): Promise<CourseAssignmentOperationResult> {
    this.ensureContext();
    this.mutating.set(true);

    try {
      const payload: ModulesScorecardsEndpointsContractsCourseAssignmentsBulkAssignRequest = {
        tournamentId: this.tournamentId!,
        roundId: this.roundId!,
        strategy,
        clearExisting,
      };

      const response = await firstValueFrom(
        this.api.bulkAssignEndpoint(payload)
      );

      // Calculate expected count from the response
      const totalPlayers = this.totalPlayers();
      const expectedAssignedCount = totalPlayers - (response.unassignedCount ?? 0);

      // Poll until the expected count is reached (event-driven scorecard creation may take time)
      await this.pollUntilConsistent({
        maxAttempts: 5,
        delayMs: 500,
        expectedAssignedCount,
      });

      return this.toOperationResult(response);
    } catch (error) {
      this.logger.error('CourseAssignmentService', 'Failed to perform bulk assignment', error);
      throw error;
    } finally {
      this.mutating.set(false);
    }
  }

  /**
   * Polls the API until the assigned count matches the expected value or max attempts are reached.
   * This handles the eventual consistency introduced by the event-driven architecture where
   * scorecards are created asynchronously via integration events.
   */
  private async pollUntilConsistent(config: PollingConfig): Promise<void> {
    const { maxAttempts, delayMs, expectedAssignedCount } = config;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      await Promise.all([this.refreshPlayers(), this.refreshSummary()]);

      const currentCount = this.assignedCount();
      if (currentCount >= expectedAssignedCount) {
        this.logger.debug(
          'CourseAssignmentService',
          `Data consistent after ${attempt} attempt(s): ${currentCount}/${expectedAssignedCount} assigned`
        );
        return;
      }

      if (attempt < maxAttempts) {
        this.logger.debug(
          'CourseAssignmentService',
          `Polling attempt ${attempt}/${maxAttempts}: ${currentCount}/${expectedAssignedCount} assigned, waiting ${delayMs}ms...`
        );
        await this.delay(delayMs);
      }
    }

    // Final refresh already done in last iteration
    this.logger.warn(
      'CourseAssignmentService',
      `Max polling attempts (${maxAttempts}) reached. Current: ${this.assignedCount()}, Expected: ${expectedAssignedCount}`
    );
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async removeAssignment(roundTournamentCourseId: number, businessEntityId: number): Promise<void> {
    this.ensureContext();
    this.mutating.set(true);

    try {
      await firstValueFrom(
        this.api.removePlayerAssignmentEndpoint(roundTournamentCourseId, businessEntityId)
      );
      await Promise.all([this.refreshPlayers(), this.refreshSummary()]);
    } catch (error) {
      this.logger.error('CourseAssignmentService', 'Failed to remove player assignment', error);
      throw error;
    } finally {
      this.mutating.set(false);
    }
  }

  private ensureContext(): void {
    if (!this.tournamentId || !this.roundId) {
      throw new Error('CourseAssignmentService must be initialized with tournament and round context before use.');
    }
  }

  private toPlayers(response: ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentPlayersResponse): CourseAssignmentPlayer[] {
    return (response.players ?? []).map(player => this.toPlayer(player));
  }

  private toPlayer(player: ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentPlayerResponse): CourseAssignmentPlayer {
    return {
      businessEntityId: player.businessEntityId ?? 0,
      playerId: player.playerId ?? '',
      firstName: player.firstName ?? '',
      lastName: player.lastName ?? '',
      fullName: player.fullName ?? '',
      rank: player.rank ?? null,
      currentAssignment: this.toAssignmentInfo(player.currentAssignment),
      previousAssignment: this.toAssignmentInfo(player.previousAssignment),
    };
  }

  private toAssignmentInfo(info?: ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentInfoResponse | null): CourseAssignmentPlayer['currentAssignment'] {
    if (!info) {
      return null;
    }

    if (info.roundTournamentCourseId === undefined || info.courseName === undefined) {
      return null;
    }

    return {
      roundTournamentCourseId: info.roundTournamentCourseId,
      courseName: info.courseName,
    };
  }

  private toCourses(response: ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentCourseResponse[] | null | undefined): CourseAssignmentCourse[] {
    return (response ?? []).map(course => ({
      roundTournamentCourseId: course.roundTournamentCourseId ?? 0,
      tournamentCourseId: course.tournamentCourseId ?? 0,
      courseId: course.courseId ?? 0,
      courseName: course.courseName ?? '',
      shortName: course.shortName ?? null,
      displayOrder: course.displayOrder ?? 0,
      active: course.active ?? false,
    }));
  }

  private toSummary(
    response: ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentSummaryResponse | null | undefined
  ): CourseAssignmentSummary | null {
    if (!response) {
      return null;
    }

    return {
      totalPlayers: response.totalPlayers ?? 0,
      assignedPlayers: response.assignedPlayers ?? 0,
      unassignedPlayers: response.unassignedPlayers ?? 0,
      percentageAssigned: response.percentageAssigned ?? 0,
      canProceedToPairings: response.canProceedToPairings ?? false,
      validationErrors: response.validationErrors ?? [],
      courses: this.toCourseSummaries(response.courses),
    };
  }

  private toCourseSummaries(
    courses?: ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentCourseSummaryResponse[] | null
  ): CourseAssignmentSummary['courses'] {
    return (courses ?? []).map(course => ({
      roundTournamentCourseId: course.roundTournamentCourseId ?? 0,
      courseName: course.courseName ?? '',
      assignedPlayerCount: course.assignedPlayerCount ?? 0,
      totalPlayers: course.totalPlayers ?? 0,
      percentageAssigned: course.percentageAssigned ?? 0,
      isComplete: course.isComplete ?? false,
    }));
  }

  private toOperationResult(
    result: ModulesScorecardsEndpointsContractsCourseAssignmentsAssignPlayersToCourseResponse | ModulesScorecardsEndpointsContractsCourseAssignmentsBulkAssignResponse
  ): CourseAssignmentOperationResult {
    return {
      assignedCount: result.assignedCount ?? 0,
      skippedCount: result.skippedCount ?? 0,
      unassignedCount: this.hasUnassignedCount(result) ? result.unassignedCount ?? 0 : undefined,
      errors: this.toOperationErrors(result.errors),
    };
  }

  private toOperationErrors(errors?: ModulesScorecardsEndpointsContractsCourseAssignmentsCourseAssignmentOperationError[] | null): CourseAssignmentOperationResult['errors'] {
    return (errors ?? []).map(error => ({
      businessEntityId: error.businessEntityId ?? 0,
      reason: error.reason ?? 'Unknown error',
    }));
  }

  private hasUnassignedCount(
    result: ModulesScorecardsEndpointsContractsCourseAssignmentsAssignPlayersToCourseResponse | ModulesScorecardsEndpointsContractsCourseAssignmentsBulkAssignResponse
  ): result is ModulesScorecardsEndpointsContractsCourseAssignmentsBulkAssignResponse {
    return 'unassignedCount' in result;
  }
}
